import { Component } from "@angular/core";
import { ShareWrapper } from "./share-wrapper.component";
import { Asset, ShareAssociatedAssetsData } from 'globals/store/assets.data';
import { environment } from "../../../environments/environment";
import { ActivatedRoute } from "@angular/router";
import { BrandingData } from "globals/store/publish.data";
import { BrandingWrapperComponent } from "src/app/components/branding/branding-wrapper.component";
import { MutatingInputComponent } from "src/app/components/joi-input/mutating-input.component";
import { DeleteButtonComponent } from "src/app/components/buttons/delete-button.component";
import { ToggleTabComponent } from "src/app/components/buttons/toggle-tab.component";
import { ActionButtonComponent } from "src/app/components/nav-bar/action-button.component";
import { CommonModule } from "@angular/common";
import { createId, getSet } from "globals/helpers/helpers";


@Component({
    selector: 'share-associated-assets',
    template: ` 
<branding-wrapper 
  [brandingData]="brandingData" 
  [style.--sitebackground]="brandingData?.template?.sitebackground || '#ffffff'">
  <div class="share-presentation-asset">
    <div *ngIf="loading" class="loading">Loading...</div>
    <div *ngIf="!loading && !data?.eventTitle"  class="sorry" [innerHTML]="helpers.strWithBreak(helpers.strWithBreak('The assets submission area is currently not available.\n\nPlease contact the event organiser.'))"></div>
    <div *ngIf="submitted"  class="thanks" [innerHTML]="helpers.strWithBreak(resultMessage)"></div>
    <div class="container" *ngIf="!!data?.eventTitle && !submitted">
      <h1>{{ data.eventTitle }}</h1>
      <div *ngIf="data.entityName" class="performer_name">
        {{ data.entityType | titlecase }}: {{ data.entityName }}
      </div>
      <div class="instructions">
        Please supply URLs to your assets.
      </div>
      <div class="note">
        Note: This form will be deactivated after you submit. Ensure you have finished modifying your assets before submitting.
      </div>

      <h5 style="margin: 10px 0">Associated Assets</h5>
      <div class="asset" *ngFor="let asset of assets">
        <mutating-input 
          [object]="asset" 
          [prop]="'name'" 
          [placeholder]="'Asset name'"
          [error]="!asset.name ? 'Asset must have a name' : null"
          (textChange)="checkValidity()"
        ></mutating-input>
        <mutating-input 
          [object]="asset" 
          [isArea]="true"
          [prop]="'description'" 
          [placeholder]="'Asset Information'"
          (textChange)="checkValidity(); changed = true;"
        ></mutating-input>
        <mutating-input 
          [object]="asset" 
          [prop]="'link'" 
          [placeholder]="'Full URL (Must start with http or https)'"
          [error]="invalids.has(asset.id) ? 'URL must start with http:// or https://' : null"
          (textChange)="checkValidity(); changed = true;"
        ></mutating-input>
        <div class="actions-holder">
          <delete-button *ngIf="!existingIds.has(asset.id)" [deleteTitle]="'Delete new asset'" (delete)="deleteAsset(asset.id)"></delete-button>
          <div style="flex:1;"></div>
          <toggle-tab
            [bottomTab]="false"
            [borderless]="true"
            [text]="asset.ready ? 'Ready' : 'Not Ready'" 
            [toggled]="asset.ready" 
            (toggle)="asset.ready = !asset.ready; checkValidity(); changed = true;"
          ></toggle-tab>
        </div>
      </div>
      <div class="actions">
        <action-button *ngIf="!!data.addNew" [text]="'Add new asset'" (click)="addAsset()"></action-button>
        <button mat-button color="primary" (click)="submit()" [disabled]="processing || !changed" >
          <div class="button_content">
            Submit
          </div>
        </button>
      </div>
      <div class="error-holder" *ngIf="error" [innerHTML]="helpers.strWithBreak(error)"></div>
    </div>
  </div>
</branding-wrapper>
  `,
    styleUrls: ['./share.editPerformer.scss'],
    styles: [`
    .actions { display: flex; margin: 10px 0; justify-content: space-between; }
    .actions-holder { display: flex; align-items: center; }
  `],
    standalone: true,
    imports: [BrandingWrapperComponent, MutatingInputComponent, DeleteButtonComponent, ToggleTabComponent, ActionButtonComponent, CommonModule]

})
export class ShareAssociatedAssetsComponent extends ShareWrapper<ShareAssociatedAssetsComponent> {
  readonly api = `${environment.functionsURL}api/v1`; // NOTE this is pointing to api/v1 not api/app
  id:string;
  data: ShareAssociatedAssetsData
  processing:boolean;
  loading: boolean
  disabled = true
  submitted:boolean;
  existingIds: Set<string> = new Set()
  assets: Asset[]
  invalids: Set<string> = new Set()
  cannotSubmitMessage = "The assets can't be submitted at this time.\n\nPlease reload this page and try again."
  resultMessage = "Thank you"
  changed = false
  brandingData: BrandingData

  constructor(private route:ActivatedRoute) {
    super()
    this.loading = true;
    document.title = "loading";
    this.route.params.subscribe(params=>{
      if(!params || !params.hasOwnProperty('id')) {
        this.loading = false;
        document.title = "Unavailable";
      } else {
        const { id } = params;
        this.id = id
        console.log({ id })
        const url = `${this.api}/load-share-associated-assets/${id}`
        this.take1(this.http.get(url)).then((data: ShareAssociatedAssetsData) => {
          this.loading = false
          console.log({ data })
          this.data = data
          this.assets = data.associatedAssets
          this.existingIds = getSet(this.assets, 'id')
          this.brandingData = data.brandingData
          document.title = "Upload asset"
        }, (response) => {
          console.log({ error: response })
          this.loading = false;
          document.title = "Unavailable";
        });
      }
    })
  }

  addAsset() {
    this.assets.push({ id: createId() }) // most of the asset modification comes when submitting
    this.disabled = true
  }

  deleteAsset(assetId: string) {
    this.assets = this.assets.filter(a => a.id !== assetId)
    this.checkValidity()
  }

  submit(){
    this.processing = true;
    this.take1(this.http.post(`${this.api}/submit-share-associated-assets`, { id: this.id, assets: this.assets })).then(_ => {
      this.processing = false;
      this.submitted = true;
    }, error => {
      console.error({ error })
      this.processing = false;
      this.showError(this.cannotSubmitMessage)
    });
  }

  isValid() {
    let valid = true
    this.invalids = new Set()
    this.assets.forEach(a => {
      const urlOk = (!a.link || (!!a.link && this.helpers.urlOk(a.link)))
      if (!urlOk) this.invalids.add(a.id)
      if (!urlOk || !a.name) valid = false
    })
    this.disabled = !valid
    return this.assets.every(a => a.name && !!a.link && this.helpers.urlOk(a.link))
  }

  checkValidity(invalid = false) {
    this.disabled = !this.isValid()
  }


}
